import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';

import { OperationService } from '../../../services';
import type {
  EnhancementStrength,
  OperationResult,
  SourceImageRef,
} from '../../../domain';
import {
  DEFAULT_ENHANCE_STATE,
  ENHANCE_OPTIONS,
  buildEnhanceRequest,
  hasSourceImage,
  type EnhanceFormState,
  type EnhanceOption,
} from './buildRequests';
import { useOperationMutation } from './useOperationMutation';
import { submitErrorMessage } from '../../../components/auth/signInRequiredError';
import type { StudioParams } from '../../shared/panels/swatchSelection';
import { StudioSwatchChips } from './StudioSwatchChips';
import { ProviderModelPicker } from './ProviderModelPicker';

interface Props {
  ensureSession: () => Promise<string>;
  isCreatingSession?: boolean;
  source: SourceImageRef | undefined;
  /** Enhance has no prompt field; chip row is informational. */
  studioParams?: StudioParams;
  onResult?: (result: OperationResult) => void;
}

const STRENGTH_OPTIONS: EnhancementStrength[] = ['Low', 'Light', 'Medium', 'High', 'Strong'];

export const EnhancePanel: React.FC<Props> = ({
  ensureSession,
  isCreatingSession = false,
  source,
  studioParams,
  onResult,
}) => {
  const [state, setState] = useState<EnhanceFormState>(DEFAULT_ENHANCE_STATE);
  const [validationError, setValidationError] = useState<string | null>(null);

  const mutation = useOperationMutation(OperationService.enhance);

  const hasSource = hasSourceImage(source);

  const toggleEnhancement = (opt: EnhanceOption) => {
    setState(s => {
      const active = s.enhancements.includes(opt);
      return {
        ...s,
        enhancements: active
          ? s.enhancements.filter(e => e !== opt)
          : [...s.enhancements, opt],
      };
    });
  };

  const handleSubmit = async () => {
    if (!source) {
      setValidationError('A source image is required to enhance.');
      return;
    }
    try {
      const sid = await ensureSession();
      const request = buildEnhanceRequest(state, sid, source);
      setValidationError(null);
      const result = await mutation.mutateAsync(request);
      onResult?.(result);
    } catch (err) {
      setValidationError(submitErrorMessage(err));
    }
  };

  const submitDisabled = mutation.isPending || isCreatingSession || !hasSource;

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1} alignItems="center">
        <AutoFixHighIcon fontSize="small" />
        <Typography variant="subtitle2">Enhance</Typography>
      </Stack>

      {!hasSource && (
        <Alert severity="info">Add or select a source image to enhance.</Alert>
      )}

      <StudioSwatchChips studioParams={studioParams} hint="not applied to Enhance" />

      <ProviderModelPicker
        model={state.model}
        onModelChange={m => setState(s => ({ ...s, model: m }))}
        disabled={mutation.isPending || isCreatingSession}
        label="Image model"
        opKind="Enhance"
      />

      <FormControl size="small" fullWidth>
        <InputLabel id="enhance-strength-label">Strength</InputLabel>
        <Select
          labelId="enhance-strength-label"
          label="Strength"
          value={state.strength}
          onChange={e => setState(s => ({ ...s, strength: e.target.value as EnhancementStrength }))}
        >
          {STRENGTH_OPTIONS.map(opt => (
            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Refinements
        </Typography>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {ENHANCE_OPTIONS.map(opt => {
            const active = state.enhancements.includes(opt);
            return (
              <Chip
                key={opt}
                label={opt}
                color={active ? 'primary' : 'default'}
                variant={active ? 'filled' : 'outlined'}
                size="small"
                onClick={() => toggleEnhancement(opt)}
              />
            );
          })}
        </Stack>
      </Box>

      {validationError && <Alert severity="warning">{validationError}</Alert>}
      {mutation.isError && (
        <Alert severity="error">
          {mutation.error instanceof Error ? mutation.error.message : 'Enhance failed.'}
        </Alert>
      )}

      <Box>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitDisabled}
          startIcon={
            mutation.isPending || isCreatingSession ? (
              <CircularProgress size={16} />
            ) : (
              <AutoFixHighIcon />
            )
          }
        >
          {isCreatingSession ? 'Starting session…' : mutation.isPending ? 'Enhancing…' : 'Enhance'}
        </Button>
      </Box>
    </Stack>
  );
};
